import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { merge, Observable, of as observableOf, Subject, Subscription } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { saveAs } from 'file-saver/FileSaver';
import { MatPaginator, MatTableDataSource, MatSort, MatSnackBar, MatDialog } from '@angular/material';
import { UserService } from '../../../../services/user.service';
import { ManagePukalService } from '../../../../services/manage-pukal.service';
import { JAN_Pukal, AccountOffice, AccountStatus, RecordStatus, PukalPagedList } from '../../../../models/pukal-reports.model';
import { SelectionModel } from '@angular/cdk/collections';
import { ManagePukalLoadingDialog } from '../../dialog.loading';
import { ManagePukalDownloadFileDialog } from '../../dialog.downloadFile';
import { AddNewAccountFederalGovernmentPukalDialog } from '../../ptj-list/federal-government/dialog.addnewaccount';
import { EditAccountFederalGovernmentPukalDialog } from '../../ptj-list/federal-government/dialog.editaccount';
import { ExportFTPFederalGovernmentDialog } from '../../ptj-list/federal-government/dialog.exporttoftp';
import { PukalAccountStatus } from '../../../../shared/enum';

@Component({
  selector: 'app-federal-government',
  templateUrl: './federal-government.component.html',
  styleUrls: ['./federal-government.component.css']
})
export class PtjListFederalGovernmentComponent implements OnInit, OnDestroy {
  allSub: Subscription[] = [];
  errorMessage: string = '';
  displayedColumns: string[] = ['Select', 'Edit', 'AgencyCode', 'Agency', 'AccOfficeCode', 'PTJ', 'PukalNo', 'Description', 'FileStatus', 'AccStatus'];
  userName: string = '';
  screenCode: string = '';
  agentCodes = [];
  accOffices = [];
  accStatus = [];
  rcdStatus = [];
  selectedAgentCode: string = '';
  selectedAccOffCode: string = '0';
  selectedAccStsCode: number = 1;
  selectedRcdStsCode: string = '0';
  warrantPTJ: string = '';
  pukalNo: string = '';
  jan_pukal: JAN_Pukal;
  p: number = 1;
  datasource: any[];
  resultsLength: number = 0;
  totalCount: number = 0;
  isLoadingResults: boolean = true;
  selectedPukalList = [];
  isOrderByPukalNo: boolean = false;
  warrant_auth_code: string = 'JA';

  selection: SelectionModel<PukalPagedList> = new SelectionModel<PukalPagedList>(true, []);
  data: PukalPagedList[];
  dataSource: MatTableDataSource<PukalPagedList>;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  recordChange$: Subject<{}> = new Subject();
  accOffices$: Observable<AccountOffice[]>;
  accStatus$: Observable<AccountStatus[]>;
  recordStatus$: Observable<RecordStatus[]>;

  constructor(private userService: UserService,
    private managePukalService: ManagePukalService,
    public snackBar: MatSnackBar,
    public dialog: MatDialog) {
    let user = this.userService.getUser();
    this.userName = user.username;
    this.screenCode = user.screenCode;

    this.accOffices$ = this.managePukalService.getAccountOffices(this.warrant_auth_code);
    this.accStatus$ = this.managePukalService.getAccountStatus();
    this.recordStatus$ = this.managePukalService.getRecordStatus();

    this.managePukalService.getPtjSearchFieldByWarrantAuthCode(this.warrant_auth_code)
      .subscribe(
        (value) => {
          this.agentCodes = value;
          this.selectedAgentCode = value[0].Airline_Agency_Code;
        },
        (error) => {
          this.errorMessage = <any>error;
          this.openSnackBar(this.errorMessage, '');
        });
  }

  ngOnInit() {
    this.jan_pukal = {
      agentCode: this.selectedAgentCode,
      accountOfficeCode: this.selectedAccOffCode,
      accountStatus: this.selectedAccStsCode.toString(),
      fileStatus: this.selectedRcdStsCode,
      warrantPTJ: this.warrantPTJ,
      pukalNo: this.pukalNo,
      page: null
    }

    merge(this.recordChange$)
      .pipe(
        startWith({}),
        switchMap(() => {
          this.isLoadingResults = true;

          return this.managePukalService.getFilteredJanPukal(this.jan_pukal);
        }),
        map(data => {
          this.isLoadingResults = false;
          this.resultsLength = data.countPukalList;

          this.dataSource = new MatTableDataSource<PukalPagedList>(data.pukalPagedList)
          this.dataSource.paginator = this.paginator;

          return data.pukalPagedList;
        }),
        catchError((error) => {
          this.isLoadingResults = false;
          this.errorMessage = <any>error;
          this.openSnackBar(this.errorMessage, '');
          return observableOf([]);
        })
      ).subscribe(data => this.data = data);
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource == null ? 0 : this.dataSource.data.length;

    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.data.forEach(row => this.selection.select(row));
  }

  searchPukalList() {
    this.selection.clear();
    this.dataSource = new MatTableDataSource<PukalPagedList>();
    this.paginator.pageIndex = 0;

    this.jan_pukal = {
      agentCode: this.selectedAgentCode,
      accountOfficeCode: this.selectedAccOffCode,
      accountStatus: this.selectedAccStsCode.toString(),
      fileStatus: this.selectedRcdStsCode,
      warrantPTJ: this.warrantPTJ,
      pukalNo: this.pukalNo,
      page: null
    };

    this.recordChange$.next();
  }

  exportIDEA() {
    let cfm = confirm("Are you sure you want to export to IDEA?");

    if (cfm) {
      this.openLoadingDialog();

      let sub = this.managePukalService.downloadJANIDEA(this.selectedAgentCode)
        .subscribe(
          (data) => {
            if (data.status === 204) {
              this.dialog.closeAll();
              this.openSnackBar('Download failed.', '');
            } else if (data.status === 200) {
              let blob = new Blob([data.body], { type: 'text/plain' });
              saveAs(blob, 'IDEA Pukal List.dat');

              this.dialog.closeAll();
            } else {
              this.dialog.closeAll();
              this.openSnackBar('Download failed.', '');
            }
          },
          error => {
            this.dialog.closeAll();
            this.errorMessage = <any>error;
            this.openSnackBar(this.errorMessage, '');
          });

      // Adds into array for each subscribe
      this.allSub.push(sub);
    }
  }

  exportList() {
    let cfm = confirm("Are you sure you want to export the List?");

    if (cfm) {

      let pukal = {
        url: "JANExportPukalList",
        agentCode: this.selectedAgentCode,
        accountOfficeCode: this.selectedAccOffCode,
        accountStatus: this.selectedAccStsCode,
        fileStatus: this.selectedRcdStsCode,
        warrantPTJ: this.warrantPTJ,
        pukalNo: this.pukalNo,
        page: 0
      }

      let dialogRef = this.dialog.open(ManagePukalDownloadFileDialog, {
        width: '250px',
        disableClose: true,
        data: { downloadParameter: pukal }
      });

      dialogRef.afterClosed().subscribe(result => {
        switch (result.status) {
          case -1:
            this.openSnackBar(result.message, '');
            break;
          case 1:
            this.openSnackBar(result.message, '');
            break;
        }
      });
    }
  }

  activeSelected() {
    var cfm = confirm("Are you sure you want to activate those selected Pukal account(s)?");

    if (cfm) {
      if (this.selection.selected.length > 0) {
        this.openLoadingDialog();

        let updatePukalModel = {
          pukals: this.selection.selected,
          AccountStatus: PukalAccountStatus.ACTIVATE
        }

        let sub = this.managePukalService.updateJANPukalAccountStatus(updatePukalModel)
          .subscribe(
            (data) => {
              this.dialog.closeAll();
              this.openSnackBar('Selected Pukal account(s) Activated.', '');
              this.searchPukalList();
              this.selectedPukalList = []; // refresh selected list
            },
            error => {
              this.selection.clear();
              this.dialog.closeAll();
              this.errorMessage = <any>error;
              this.openSnackBar(this.errorMessage, '');
            });

        // Adds into array for each subscribe
        this.allSub.push(sub);
      } else {
        this.openSnackBar('No Pukal account is selected. Please make sure at least one Pukal account is selected.', '');
      }
    }
  }

  deactiveSelected() {
    var cfm = confirm("Are you sure you want to deactivate those selected Pukal account(s)?");

    if (cfm) {
      if (this.selection.selected.length > 0) {
        this.openLoadingDialog();

        let updatePukalModel = {
          pukals: this.selection.selected,
          AccountStatus: PukalAccountStatus.DEACTIVATE
        }

        let sub = this.managePukalService.updateJANPukalAccountStatus(updatePukalModel)
          .subscribe(
            (data) => {
              this.dialog.closeAll();
              this.openSnackBar('Selected Pukal account(s) Deactivated.', '');
              this.searchPukalList();
              this.selectedPukalList = []; // refresh selected list
            },
            error => {
              this.selection.clear();
              this.dialog.closeAll();
              this.errorMessage = <any>error;
              this.openSnackBar(this.errorMessage, '');
            });

        // Adds into array for each subscribe
        this.allSub.push(sub);
      } else {
        this.openSnackBar('No Pukal account is selected. Please make sure at least one Pukal account is selected.', '');
      }
    }
  }

  terminateSelected() {
    var cfm = confirm("Are you sure you want to terminate those selected Pukal account(s)?");

    if (cfm) {
      if (this.selection.selected.length > 0) {
        //loading dialog
        this.openLoadingDialog();

        let updatePukalModel = {
          pukals: this.selection.selected,
          AccountStatus: PukalAccountStatus.TERMINATE
        }

        let sub = this.managePukalService.updateJANPukalAccountStatus(updatePukalModel)
          .subscribe(
            (data) => {
              this.dialog.closeAll();
              this.openSnackBar('Selected Pukal account(s) Terminated.', '');
              this.searchPukalList();
              this.selectedPukalList = []; // refresh selected list
            },
            error => {
              this.selection.clear();
              this.dialog.closeAll();
              this.errorMessage = <any>error;
              this.openSnackBar(this.errorMessage, '');
            });

        // Adds into array for each subscribe
        this.allSub.push(sub);
      } else {
        this.openSnackBar('No Pukal account is selected. Please make sure at least one Pukal account is selected.', '');
      }
    }
  }

  exportResetPtj() {
    var cfm = confirm("Are you sure you want to reset those selected Pukal account(s)?");

    if (cfm) {
      if (this.selection.selected.length > 0) {
        //loading dialog
        this.openLoadingDialog();

        let sub = this.managePukalService.exportResetJANPukal(this.selection.selected)
          .subscribe(
            (data) => {
              if (data.status === 204) {
              } else if (data.status === 200) {
                this.dialog.closeAll();
                this.openSnackBar('Selected Pukal account(s) Export Reseted.', '');
                this.searchPukalList();
                this.selectedPukalList = []; // refresh selected list
              } else {
              }
            },
            error => {
              this.selection.clear();
              this.dialog.closeAll();
              this.errorMessage = <any>error;
              this.openSnackBar(this.errorMessage, '');
            });

        // Adds into array for each subscribe
        this.allSub.push(sub);
      } else {
        this.openSnackBar('No Pukal account is selected. Please make sure at least one Pukal account is selected.', '');
      }
    }
  }

  exportToFTPDialog(): void {
    let dialogRef = this.dialog.open(ExportFTPFederalGovernmentDialog, {
      width: '80%',
      disableClose: true,
      data: {}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'Download successful') {
        this.searchPukalList();
      }
    });
  }

  openLoadingDialog(): void {
    let dialogRef = this.dialog.open(ManagePukalLoadingDialog, {
      disableClose: true,
      width: '250px'
    });

    dialogRef.afterClosed().subscribe(result => {
      //console.log('The dialog was closed');
    });
  }

  openEditPukalListDialog(accData: any): void {
    let dialogRef = this.dialog.open(EditAccountFederalGovernmentPukalDialog, {
      width: '60%',
      disableClose: true,
      data: { selectedAcc: accData, screenCode: this.screenCode }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'Edit successful') {
        this.searchPukalList();
        this.openSnackBar('Edit successful.', '');
      }
    });
  }

  openAddNewAccDialog(): void {
    let dialogRef = this.dialog.open(AddNewAccountFederalGovernmentPukalDialog, {
      width: '60%',
      disableClose: true,
      data: {}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'Add new successful') {
        this.searchPukalList();
        this.openSnackBar('Add new account successful.', '');
      }
    });
  }

  ngOnDestroy() {
    for (let sub of this.allSub) {
      sub.unsubscribe();
    }
  }

  openSnackBar(message: string, action: string) {
    this.snackBar.open(message, action, {
      duration: 3000,
    });
  }
}
